// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Platform } from 'react-native';
import {
  MINI_PROFILE_CARD_DEFAULT_HEIGHT,
  MINI_PROFILE_CARD_DEFAULT_WIDTH,
  clampToViewport,
  readAnchorMeasurement,
} from './anchorGeometry';
import type { MiniProfileAnchorRect, MiniProfileSide } from './types';

export interface UseMiniProfileAnchorArgs {
  readonly anchorRef: RefObject<unknown>;
  readonly side?: MiniProfileSide;
  readonly offset?: number;
  readonly cardWidth?: number;
  readonly cardHeight?: number;
}

export interface UseMiniProfileAnchorResult {
  readonly rect: MiniProfileAnchorRect;
  readonly remeasure: () => void;
}

const HIDDEN_RECT: MiniProfileAnchorRect = {
  top: 0,
  left: 0,
  visible: false,
};

/**
 * Hook that resolves a mini-profile popover position from a measured anchor
 * `RefObject`. Re-runs on `window` `resize` (web/macOS) so the card stays
 * pinned when the chrome reflows.
 *
 * Geometry is delegated to {@link clampToViewport} (pure helper in
 * `./anchorGeometry`) so the math can be exercised without mounting.
 */
export function useMiniProfileAnchor(args: UseMiniProfileAnchorArgs): UseMiniProfileAnchorResult {
  const {
    anchorRef,
    side = 'bottom',
    offset = 8,
    cardWidth = MINI_PROFILE_CARD_DEFAULT_WIDTH,
    cardHeight = MINI_PROFILE_CARD_DEFAULT_HEIGHT,
  } = args;

  const [rect, setRect] = useState<MiniProfileAnchorRect>(HIDDEN_RECT);
  const lastValidRect = useRef<MiniProfileAnchorRect>(HIDDEN_RECT);

  const remeasure = useCallback(() => {
    const measurement = readAnchorMeasurement(anchorRef);
    if (measurement === null) {
      setRect(lastValidRect.current);
      return;
    }
    const viewport = readViewport();
    const next = clampToViewport({
      anchor: measurement,
      side,
      offset,
      cardWidth,
      cardHeight,
      viewport,
    });
    lastValidRect.current = next;
    setRect(next);
  }, [anchorRef, side, offset, cardWidth, cardHeight]);

  useEffect(() => {
    remeasure();
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const handler = (): void => remeasure();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [remeasure]);

  return { rect, remeasure };
}

function readViewport(): { width: number; height: number } {
  if (typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
    return { width: window.innerWidth, height: window.innerHeight };
  }
  // Defensive default — large enough that clamping is a no-op for typical
  // anchors, small enough that nothing renders off-screen.
  return { width: 1024, height: 768 };
}
